import { type FC } from "react";
import { Users, X } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { IconButton } from "@/shared/components/ui/icon-button";
import { useUserCustomizations } from "@/shared/hooks/useUserCustomizations";
import { sentryUtils } from "@/shared/utils/sentry";
import { getBannerDismissKey } from "./contactUtils";
import AttorneySigning from "@/shared/assets/illustrations/attorney-signing.png";
import InviteFamilyMember from "@/shared/assets/illustrations/invite-family-member.svg";

interface AttorneyBannerProps {
  onAddAttorney: () => void;
  onAddContact: () => void;
  hasAttorney: boolean;
  disabled?: boolean;
}

export const AttorneyBanner: FC<AttorneyBannerProps> = ({
  onAddAttorney,
  onAddContact,
  hasAttorney,
  disabled = false,
}) => {
  const { setCustomization, isUpdating } = useUserCustomizations();
  const isDisabled = disabled || isUpdating;

  const handleDismiss = async () => {
    if (isDisabled) return;
    try {
      await setCustomization(getBannerDismissKey(hasAttorney), true);
    } catch (err) {
      sentryUtils.captureError(err, "Failed to dismiss attorney banner");
    }
  };

  const illustration = hasAttorney ? InviteFamilyMember : AttorneySigning;
  const illustrationAlt = hasAttorney ? "Invite family" : "Invite your attorney";
  const bgColor = hasAttorney ? "bg-salmon-50" : "bg-blue-50";
  const isPng = !hasAttorney;

  return (
    <div className="flex flex-col md:flex-row items-center overflow-hidden rounded-lg border border-primary bg-white relative">
      <IconButton
        onClick={handleDismiss}
        className="absolute top-2 right-2 z-10"
        label="Dismiss banner"
        tooltip={false}
        disabled={isDisabled}
      >
        <X />
      </IconButton>

      <div
        className={`hidden md:flex shrink-0 md:w-[200px] md:h-[200px] ${bgColor} overflow-hidden items-center justify-center`}
      >
        <img
          src={illustration}
          alt={illustrationAlt}
          className={`size-full object-cover ${isPng ? "scale-110 mix-blend-multiply" : ""}`}
        />
      </div>

      <div className="w-full flex-1 min-w-0 p-4 pr-12 md:p-6 flex flex-col justify-center">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            {hasAttorney ? (
              <>
                <p className="text-base font-semibold">Invite family and beneficiaries</p>
                <p className="text-sm text-secondary">
                  Add family members, friends, or professionals (lawyers and financial advisors) so
                  they can follow the roadmap and support you along the way.
                </p>
              </>
            ) : (
              <>
                <p className="text-base font-semibold">Give your attorney access in one step</p>
                <p className="text-sm text-secondary">
                  Invite your attorney so they can follow the estate's progress, review key
                  information, and support you along the way.
                </p>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {hasAttorney ? (
              <Button
                variant="brand"
                size="md"
                onClick={onAddContact}
                disabled={isDisabled}
                title={disabled ? "View-only mode during simulation" : undefined}
              >
                <Users className="h-4 w-4" />
                Invite
              </Button>
            ) : (
              <>
                <Button
                  variant="brand"
                  size="md"
                  onClick={onAddAttorney}
                  disabled={isDisabled}
                  title={disabled ? "View-only mode during simulation" : undefined}
                >
                  <Users className="h-4 w-4" />
                  Invite attorney
                </Button>
                <Button
                  variant="ghost"
                  size="md"
                  onClick={handleDismiss}
                  disabled={isDisabled}
                  className="bg-white text-primary hover:underline hover:bg-white"
                >
                  No attorney?
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
